<template>
  <nav aria-label="breadcrumb" class="mb-2" v-if="breadcrumbItems.length > 0">
    <ol class="breadcrumb mb-0 p-2">
      <!-- Dashboard siempre visible -->
      <li class="breadcrumb-item">
        <router-link :to="{ name: 'Dashboard' }">
          <i class="fas fa-home"></i> Dashboard
        </router-link>
      </li>

      <!-- Items del breadcrumb -->
      <li
        v-for="(item, index) in breadcrumbItems"
        :key="index"
        class="breadcrumb-item"
        :class="{ active: item.active }"
      >
        <router-link v-if="item.to && !item.active" :to="item.to">
          <i v-if="item.icon" :class="item.icon"></i>
          {{ item.label }}
        </router-link>
        <span v-else>
          <i v-if="item.icon" :class="item.icon"></i>
          {{ item.label }}
        </span>
      </li>
    </ol>
  </nav>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import {
  currentItemLabel,
  labelFromTitleSlug,
} from "@/utils/currentItemLabel";
import { parseDynamicItemPrettySlug } from "@/utils/dynamicModelPaths";

export default {
  name: "Breadcrumb",
  data() {
    return {
      breadcrumbItems: [],
      models: [],
      modelsLoaded: false,
    };
  },
  computed: {
    modelName() {
      return this.$route.params.modelName;
    },
    itemId() {
      return this.$route.params.itemId;
    },
    routeName() {
      return this.$route.name;
    },
    /** Nombre publicado por la ficha; se observa para redibujar cuando llega. */
    publishedItemLabel() {
      return currentItemLabel.itemId === String(this.itemId || "")
        ? currentItemLabel.label
        : "";
    },
    /** Parte legible del slug de la URL /i/…, como respaldo mientras carga la ficha. */
    titleSlugFromRoute() {
      const slug = this.$route.params && this.$route.params.itemSlug;
      if (!slug) return "";
      const parsed = parseDynamicItemPrettySlug(slug, this.modelName ? [this.modelName] : []);
      return parsed ? parsed.titleSlug || "" : "";
    },
    listMode() {
      const path = this.$route.path || "";
      if (path.endsWith("/trash")) return "trash";
      if (path.endsWith("/archive")) return "archive";
      return "normal";
    },
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler() {
        this.updateBreadcrumb();
      },
    },
    publishedItemLabel() {
      this.updateBreadcrumb();
    },
  },
  methods: {
    async fetchModels() {
      if (this.modelsLoaded) return;
      try {
        const { data } = await wencesApi.get("/api/gotrading/model-definitions");
        this.models = data || [];
        this.modelsLoaded = true;
      } catch (error) {
        console.error("Error loading models:", error);
        this.models = [];
      }
    },

    getModelListRoute(modelName, listMode = "normal") {
      if (listMode === "trash") {
        return { name: "DynamicModelTrash", params: { modelName } };
      }
      if (listMode === "archive") {
        return { name: "DynamicModelArchive", params: { modelName } };
      }
      return { name: "DynamicModel", params: { modelName } };
    },

    listModeCrumbLabel() {
      return this.listMode === "trash"
        ? this.$t("listTitleTrash")
        : this.$t("listTitleArchive");
    },

    listModeCrumbIcon() {
      return this.listMode === "trash"
        ? "fas fa-trash-alt"
        : "fas fa-archive";
    },

    appendListModeCrumb(items) {
      if (this.itemId || this.listMode === "normal") return;
      items.push({
        label: this.listModeCrumbLabel(),
        icon: this.listModeCrumbIcon(),
        to: null,
        active: true,
      });
    },

    listCrumbTo(modelName) {
      if (this.itemId || this.listMode !== "normal") {
        return this.getModelListRoute(modelName, "normal");
      }
      return null;
    },

    isListCrumbActive() {
      return !this.itemId && this.listMode === "normal";
    },

    async updateBreadcrumb() {
      if (!this.modelName) {
        // Pantallas fijas (fuera de los modelos): declaran su miga en meta.breadcrumb.
        const crumb = this.$route.meta && this.$route.meta.breadcrumb;
        this.breadcrumbItems = crumb
          ? [{ label: this.$tn(crumb.label), icon: crumb.icon, to: null, active: true }]
          : [];
        return;
      }

      // Cargar modelos si no están cargados
      if (!this.modelsLoaded) {
        await this.fetchModels();
      }

      const items = [];
      const modelNameLower = this.modelName.toLowerCase();

      // Buscar el modelo
      const model = this.models.find(
        m => m.modelName.toLowerCase() === modelNameLower
      );

      // Es un modelo derivado? (ej: shipping_expenses)
      const isDerived = !model && this.modelName.includes('_');

      if (isDerived) {
        // Modelo derivado - agregar modelo padre primero
        const derivedInfo = this.findDerivedModelInfo(this.modelName);
        
        if (derivedInfo) {
          // Modelo padre (listado)
          items.push({
            label: this.$t(derivedInfo.parentModel.label || derivedInfo.parentModel.modelName),
            icon: derivedInfo.parentModel.icon ? `fas fa-${derivedInfo.parentModel.icon}` : 'fas fa-list',
            to: { name: 'DynamicModel', params: { modelName: derivedInfo.parentModel.modelName } },
            active: false,
          });

          // Campo derivado (listado)
          items.push({
            label: derivedInfo.field.label || derivedInfo.fieldName,
            icon: 'fas fa-layer-group',
            to: this.listCrumbTo(this.modelName),
            active: this.isListCrumbActive(),
          });
        } else {
          // No se encontró info, mostrar el nombre formateado
          items.push({
            label: this.formatModelName(this.modelName),
            icon: 'fas fa-list',
            to: this.listCrumbTo(this.modelName),
            active: this.isListCrumbActive(),
          });
        }
      } else if (model) {
        // Modelo principal - agregar listado
        items.push({
          label: this.$t(model.label || model.modelName),
          icon: model.icon ? `fas fa-${model.icon}` : 'fas fa-list',
          to: this.listCrumbTo(model.modelName),
          active: this.isListCrumbActive(),
        });
      } else {
        // Modelo no encontrado
        items.push({
          label: this.modelName,
          icon: 'fas fa-list',
          to: this.listCrumbTo(this.modelName),
          active: this.isListCrumbActive(),
        });
      }

      this.appendListModeCrumb(items);

      // Si hay itemId, agregar el item
      if (this.itemId) {
        // Sin pedir nada al API: la ficha ya trae el item y publica su nombre. Antes acá
        // se hacía un GET del documento entero (~200 KB) para leer un solo campo, en
        // paralelo con el que ya estaba haciendo la ficha.
        const itemLabel =
          this.publishedItemLabel ||
          labelFromTitleSlug(this.titleSlugFromRoute) ||
          `#${this.itemId.slice(-6)}`;

        const isEdit = this.routeName === 'DynamicModelEdit';
        
        if (isEdit) {
          // Vista de edición - agregar item como link y "Editar" como activo
          items.push({
            label: itemLabel,
            icon: 'fas fa-eye',
            to: { name: 'DynamicModelDetail', params: { modelName: this.modelName, itemId: this.itemId } },
            active: false,
          });
          items.push({
            label: this.$t('Edit') || 'Editar',
            icon: 'fas fa-edit',
            to: null,
            active: true,
          });
        } else {
          // Vista de detalle
          items.push({
            label: itemLabel,
            icon: 'fas fa-eye',
            to: null,
            active: true,
          });
        }
      }

      this.breadcrumbItems = items;
    },

    findDerivedModelInfo(derivedModelName) {
      const nameLower = derivedModelName.toLowerCase();
      
      for (const model of this.models) {
        for (const field of model.fields || []) {
          if (field.type === 'group' && field.isArray) {
            const expectedName = `${model.modelName.toLowerCase()}_${field.key.toLowerCase()}`;
            if (expectedName === nameLower) {
              return {
                parentModel: model,
                field: field,
                fieldName: field.key,
              };
            }
          }
        }
      }
      return null;
    },

    formatModelName(name) {
      return name
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    },
  },
};
</script>

<style scoped>
.breadcrumb {
  background-color: #f8f9fa;
  padding: 0.5rem 1rem;
  margin-bottom: 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
}

.breadcrumb-item i {
  font-size: 14px;
  margin-right: 4px;
}

.breadcrumb-item a {
  display: flex;
  align-items: center;
  color: var(--bs-primary);
  text-decoration: none;
}

.breadcrumb-item a:hover {
  color: var(--bs-primary);
  opacity: 0.9;
  text-decoration: underline;
}

.breadcrumb-item.active {
  color: #6c757d;
}

.breadcrumb-item.active span {
  display: flex;
  align-items: center;
}

.breadcrumb-item + .breadcrumb-item::before {
  content: "›";
  padding: 0 0.5rem;
  color: #6c757d;
}
</style>
